import React, { useState, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import * as Sentry from '@sentry/browser';
import Link from 'redux-first-router-link';
import cartAnalytics from './cartAnalytics';
import Price from '../Product/Price';
import Button from '../Elements/Button/Button';
import Select from '../Elements/Select/Select';
import LazyImage from '../../hocs/Lazy/LazyImage';
import { addToBag } from '../../actions/checkout';
import { unique, differenceLeft } from '../../utils/array';
import { productImageSizeTranslation } from '../../utils/image';
import { prefix, translate } from '../../utils/locale';
import { formatPriceByExample } from '../../utils/string';
import { deepClone } from '../../utils/object';
import s from './CartAddOns.scss';

const CartAddOns = ({ productCache, items, setup, isSelection = false, ...props }) => {
	const products = productCache?.[setup.market]?.[setup.pricelist]?.products;
	const [selectedProduct, updateSelection] = useState(null);

	const selectSizeHandler = (id, value) => {
		updateSelection(value);
	};

	useEffect(() => {
		if (selectedProduct) addToBagHandler(false, selectedProduct.slice(0, selectedProduct.indexOf('-')));
	}, [selectedProduct]);

	const addToBagHandler = (e, item = false) => {
		if (e) {
			e.preventDefault();
			item = e.currentTarget.dataset.item;
		}
		const cachedProduct = products[item];
		const currentCategory = cachedProduct.canonicalUri.slice(0, cachedProduct.canonicalUri.lastIndexOf('/'));
		const selected = selectedProduct || cachedProduct.sizes[0].item;

		if (selected) {
			props.addToBag(selected, 1, currentCategory, cachedProduct).then((response) => {
				cartAnalytics(cachedProduct, selected, setup, false, currentCategory, response?.selection);
				updateSelection(null);
				return response;
			}).catch((err) => {
				console.log('DEBUG: first product unsuccessfully added to bag', err);
				Sentry.captureException(err);
			});
		}
	};

	if (!products || !items || setup.campaignType) return null;

	let relatedProducts = items.reduce((acc, item) => {
		const productId = item.item.slice(0, item.item.indexOf('-'));
		if (!products[productId] || !products[productId].relatedAddOns || !products[productId].inStock) return acc;
		return [...acc, ...products[productId].relatedAddOns];
	}, []);
	relatedProducts = unique(relatedProducts);
	relatedProducts = differenceLeft(relatedProducts, items.map((i) => i.item.slice(0, i.item.indexOf('-'))));

	relatedProducts = relatedProducts.filter((rP) => {
		const productCategories = products?.[rP]?.categories ? Object.keys(products[rP].categories).map((c) => products[rP].categories?.[c]?.uri || '').join('') : '';
		if (!products?.[rP] || products?.[rP].inStock === 0) return false;
		return productCategories.indexOf('accessories') !== -1;
	});

	if (relatedProducts.length === 0) return null;

	return (
		<div className={[s.cartAddOns, isSelection ? s.cartAddOns__selection : null].join(' ')}>
			{
				!isSelection && (
					<>
						<span className={s.cartAddOns_title}>{ translate(setup, 'cartAddOnsTitle') }</span>
						<span className={s.cartAddOns_subtitle}>{ translate(setup, 'cartAddOnsSubtitle') }</span>
					</>
				)
			}
			<div className={s.cartAddOns_items}>
				{
					relatedProducts.map((item) => {
						const price = deepClone(products[item].price);
						let discount = 0.5;
						if (products[item].features) {
							discount = Object.keys(products[item].features)
								.filter((d) => {
									if (products[item].features?.[d]?.featureId === 'addonDiscount') {
										if (products[item].features[d].incPricelist && products[item].features[d].incPricelist.split(/\s*,\s*/).indexOf(setup.pricelist) === -1) return false;
										if (products[item].features[d].incMarket && products[item].features[d].incMarket.split(/\s*,\s*/).indexOf(String(setup.market)) === -1) return false;
										if (products[item].features[d].incCountry && products[item].features[d].incCountry.split(/\s*,\s*/).indexOf(setup.country) === -1) return false;
										return true;
									}
									return false;
								})
								.reduce((acc, d) => (parseInt(products[item].features[d].feature, 10) / 100), 0.5);
						}
						price.showAsOnSale = true;
						price.price = formatPriceByExample(price.price, Math.round(price.priceBeforeDiscountAsNumber * (1 - discount)));
						return (
							<div key={item} className={s.cartAddOns_item}>
								<Link className={s.cartAddOns_item_link} to={prefix(setup, products[item].canonicalUri)}>
									{ products[item].image && <LazyImage {...productImageSizeTranslation(3, products[item].image[0], false, false, 160)} width="80" /> }
									<Price price={price} soldOut={!products[item].inStock ? translate(setup, 'soldOut') : false} />
								</Link>
								<form className={s.cartAddOns_item_form} onSubmit={addToBagHandler} data-item={item}>
									{
										products[item].sizes.length > 1 && (
											<Select id={`addOn-${item}`} variantClasses={['select__addOn']} options={[{ key: '', value: translate(setup, 'chooseSize') }, ...products[item].sizes.map((size) => ({ key: size.item, value: size.name, disabled: size.stock === 0 }))]} inputChange={selectSizeHandler} />
										)
									}
									<Button variantClasses={['button__addOn']} type="submit">
										<span>{ translate(setup, 'cartAddOnsAdd') }</span>
									</Button>
								</form>
							</div>
						);
					})
				}
			</div>
		</div>
	);
};

const mapState = ({ productCache }, props) => ({ productCache, ...props });

export default hot(module)(connect(mapState, { addToBag })(CartAddOns));
